import qz from "qz-tray";
import { api } from "../../redux/apis/api";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import moment from "moment";

// Prints thermal labels on TSC printers (TTP-244 Pro and other TSPL models)
// by sending raw TSPL, so the printer draws every bar itself at whole dots —
// no driver scaling, no print dialog and no page-size guessing. Two free
// transports:
//  - desktop app: Electron sends the bytes to the Windows spooler
//    ("print-raw" in public/electron.js), nothing extra to install;
//  - browser: QZ Tray (free, open source) running on the PC.

const DOTS_PER_MM = 8; // 203 dpi
const PAD = DOTS_PER_MM; // 1mm inner padding
// Built-in TSPL bitmap fonts at 203 dpi: [font id, char width, line height].
const FONT_SMALL = { id: "1", charWidth: 8, lineHeight: 14 };
const FONT_MEDIUM = { id: "2", charWidth: 12, lineHeight: 22 };
const FONT_LARGE = { id: "3", charWidth: 16, lineHeight: 26 };
const FONT_XLARGE = { id: "4", charWidth: 24, lineHeight: 34 };
// Largest first: a label whose only text is its title gets the biggest that fits.
const TITLE_FONTS = [FONT_XLARGE, FONT_LARGE, FONT_MEDIUM, FONT_SMALL];
const MAX_QR_CELL_DOTS = 8;
// Blank modules kept around a title label's QR (from the label edge and the
// text) so scanners can find it; 2 still lets a 25mm label hold a large QR.
const QR_QUIET_MODULES = 2;
// CODE128 needs a 10-module quiet zone on each side.
const QUIET_ZONE_MODULES = 10;
const MIN_BARCODE_HEIGHT = 4 * DOTS_PER_MM;

// Keep in step with the qz-tray version in package.json.
const QZ_TRAY_VERSION = "2.3.0";
const QZ_DOWNLOAD_PAGE_URL = "https://qz.io/download/";
const QZ_WINDOWS_INSTALLER_URL = `https://github.com/qzind/tray/releases/download/v${QZ_TRAY_VERSION}/qz-tray-${QZ_TRAY_VERSION}-x86_64.exe`;

const PRINTER_STORAGE_KEY = "thermalLabelPrinterName";
const CONNECT_TIMEOUT_MS = 6000;

export function clientOsName() {
  const platform = `${navigator.userAgentData?.platform || ""} ${
    navigator.platform || ""
  } ${navigator.userAgent || ""}`;
  if (/win/i.test(platform)) return "Windows";
  if (/mac/i.test(platform)) return "macOS";
  if (/linux/i.test(platform)) return "Linux";
  return "";
}

// The desktop app exposes raw printing through its preload script.
export function isDesktopApp() {
  return typeof window.electron?.printRaw === "function";
}

// Windows gets the installer directly (one click); macOS/Linux installers
// depend on the CPU, which the browser can't reliably tell, so they get
// the downloads page.
export function qzDownloadUrl() {
  return clientOsName() === "Windows"
    ? QZ_WINDOWS_INSTALLER_URL
    : QZ_DOWNLOAD_PAGE_URL;
}

// Inside the desktop (Electron) app a plain link would open in an app
// window, so hand it to the OS browser; in Chrome it opens a new tab.
export function openQzDownload() {
  const url = qzDownloadUrl();
  const ipc = window.electron?.ipcRenderer;
  if (ipc) {
    ipc.send("open-external", url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

export function getSavedThermalPrinter() {
  try {
    return localStorage.getItem(PRINTER_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function saveThermalPrinter(name) {
  try {
    localStorage.setItem(PRINTER_STORAGE_KEY, name || "");
  } catch {}
}

// Connection states shown in the print modal.
export const PRINT_STATE = {
  OPEN: "open",
  // Still dialing QZ Tray — usually waiting on a browser "local network
  // access" prompt or QZ Tray's "allow this site" prompt.
  CONNECTING: "connecting",
  NOT_RUNNING: "notRunning",
  // The page is served over plain http from a non-localhost address, and
  // the browser blocks such pages from reaching apps on localhost.
  INSECURE_PAGE: "insecurePage",
};

// http://localhost counts as secure; http://<server-ip or name> does not.
function pageCanReachLocalhost() {
  return window.isSecureContext !== false;
}

// QZ Tray only lets a site be "always allowed" (or print silently) when its
// requests are signed. The certificate comes from the backend
// (GET /api/qz/certificate) and the backend signs each request with its
// private key (GET /api/qz/sign). Without that set up, QZ Tray still works but
// asks "Allow" every time.
// The env vars below override the backend endpoints if needed:
//   REACT_APP_QZ_CERTIFICATE  public certificate (PEM text)
//   REACT_APP_QZ_SIGN_URL     GET <url>?request=<data> returning the
//                             SHA512 signature (base64) made with its key
let qzConfigured = false;
let qzSigned = false;
function configureQz() {
  if (qzConfigured) return;
  qzConfigured = true;
  const envCertificate = process.env.REACT_APP_QZ_CERTIFICATE || "";
  const signUrl = process.env.REACT_APP_QZ_SIGN_URL || "";
  qz.security.setCertificatePromise((resolve) => {
    if (envCertificate) {
      qzSigned = true;
      resolve(envCertificate);
      return;
    }
    api
      .get("/qz/certificate", { responseType: "text", transformResponse: (d) => d })
      .then((res) => {
        const cert = typeof res.data === "string" && res.data.includes("BEGIN CERTIFICATE") ? res.data : "";
        qzSigned = !!cert;
        resolve(cert);
      })
      .catch(() => {
        qzSigned = false;
        resolve(); // not set up on the server: unsigned
      });
  });
  qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    if (signUrl) {
      fetch(`${signUrl}?request=${encodeURIComponent(toSign)}`)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.statusText)))
        .then(resolve, reject);
      return;
    }
    if (!qzSigned) {
      resolve();
      return;
    }
    api
      .get("/qz/sign", { params: { request: toSign }, responseType: "text", transformResponse: (d) => d })
      .then((res) => resolve(res.data), reject);
  });
  qz.websocket.setClosedCallbacks(() => {
    cachedQzPrinters = null;
    if (statusListener) statusListener(PRINT_STATE.NOT_RUNNING);
  });
}

// true when QZ requests are signed with the site certificate
export function isQzSigned() {
  return qzSigned;
}

let qzAttempt = null;
// Set when a caller stopped waiting on qzAttempt (it resolved CONNECTING),
// so only then is the late result pushed through statusListener.
let qzAttemptOutlived = false;
let statusListener = null;
let lastQzError = "";
// Every QZ Tray request from an unsigned site raises an "Allow" prompt, so
// the printer list is fetched once per connection and reused.
let cachedQzPrinters = null;
let qzFindPromise = null;

// Why the last QZ Tray connection failed, shown in the modal to diagnose.
export function getLastPrinterError() {
  return lastQzError;
}

// Called when the QZ Tray connection changes after the modal stopped
// waiting (e.g. the user clicked Allow late), so it can reload printers.
export function onPrinterStatusChange(listener) {
  statusListener = listener;
  return () => {
    if (statusListener === listener) statusListener = null;
  };
}

/**
 * Connects to QZ Tray and resolves a PRINT_STATE. An attempt still pending
 * after CONNECT_TIMEOUT_MS resolves CONNECTING but keeps running, and
 * reports through onPrinterStatusChange once it settles. A failed attempt
 * leaves the socket closed, so the next call simply dials again.
 */
export function connectQz() {
  configureQz();
  if (qz.websocket.isActive()) return Promise.resolve(PRINT_STATE.OPEN);
  if (!qzAttempt) {
    qzAttempt = qz.websocket
      .connect({ retries: 0 })
      .then(
        () => {
          lastQzError = "";
          return PRINT_STATE.OPEN;
        },
        (error) => {
          lastQzError = String(error?.message || error || "");
          console.warn("QZ Tray connection failed:", error);
          return pageCanReachLocalhost()
            ? PRINT_STATE.NOT_RUNNING
            : PRINT_STATE.INSECURE_PAGE;
        }
      )
      .then((state) => {
        qzAttempt = null;
        if (qzAttemptOutlived && statusListener) statusListener(state);
        qzAttemptOutlived = false;
        return state;
      });
  }
  return Promise.race([
    qzAttempt,
    new Promise((resolve) =>
      setTimeout(() => {
        qzAttemptOutlived = true;
        resolve(PRINT_STATE.CONNECTING);
      }, CONNECT_TIMEOUT_MS)
    ),
  ]);
}

const LAUNCH_TIMEOUT_MS = 25000;
const LAUNCH_POLL_MS = 1500;

/**
 * Starts an installed-but-stopped QZ Tray. Its installer registers the
 * "qz:" URL scheme, so opening qz:launch starts the app (the browser may
 * first ask to open QZ Tray). Then keeps dialing until it answers, since
 * it takes a few seconds to start. Resolves the final PRINT_STATE.
 */
export async function launchQzTray() {
  window.location.assign("qz:launch");
  const deadline = Date.now() + LAUNCH_TIMEOUT_MS;
  let state = PRINT_STATE.NOT_RUNNING;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, LAUNCH_POLL_MS));
    state = await connectQz();
    // Anything but "nothing is listening yet" is final (OPEN, a pending
    // permission prompt, or a blocked page).
    if (state !== PRINT_STATE.NOT_RUNNING) break;
  }
  return state;
}

// Chrome's "Apps on device" site permission (reaching apps on localhost,
// like QZ Tray). Its name changed across Chrome versions; unknown names
// make query() throw, so try each and use the first one Chrome knows.
const APPS_ON_DEVICE_PERMISSIONS = ["loopback-network", "local-network-access"];
const PERMISSION_WAIT_MS = 60000;

async function queryAppsOnDevicePermission() {
  if (!navigator.permissions?.query) return null;
  for (const name of APPS_ON_DEVICE_PERMISSIONS) {
    try {
      return await navigator.permissions.query({ name });
    } catch {}
  }
  return null;
}

export const APPS_ON_DEVICE = {
  GRANTED: "granted",
  DENIED: "denied",
  // The user hasn't answered Chrome's prompt within PERMISSION_WAIT_MS.
  PENDING: "pending",
  // Desktop app, or a browser without this permission: nothing to ask.
  NOT_NEEDED: "notNeeded",
};

/**
 * Makes sure the page may reach QZ Tray on this computer. When Chrome
 * hasn't asked yet, starts the QZ Tray connection — that is what makes
 * Chrome show its "Apps on device" prompt — and waits for the answer.
 * onPrompt is called if the user has to answer the prompt.
 */
export async function ensureAppsOnDevicePermission({ onPrompt } = {}) {
  if (isDesktopApp()) return APPS_ON_DEVICE.NOT_NEEDED;
  const status = await queryAppsOnDevicePermission();
  if (!status) return APPS_ON_DEVICE.NOT_NEEDED;
  if (status.state === "granted") return APPS_ON_DEVICE.GRANTED;
  if (status.state === "denied") return APPS_ON_DEVICE.DENIED;

  if (onPrompt) onPrompt();
  return new Promise((resolve) => {
    const finish = (state) => {
      clearTimeout(timer);
      status.onchange = null;
      resolve(state);
    };
    const timer = setTimeout(
      () => finish(APPS_ON_DEVICE.PENDING),
      PERMISSION_WAIT_MS
    );
    status.onchange = () => {
      if (status.state === "granted") finish(APPS_ON_DEVICE.GRANTED);
      else if (status.state === "denied") finish(APPS_ON_DEVICE.DENIED);
    };
    // Reaching QZ Tray is what triggers Chrome's prompt; it also warms up
    // the connection the modal needs next.
    connectQz().then((state) => {
      if (state === PRINT_STATE.OPEN) finish(APPS_ON_DEVICE.GRANTED);
    });
  });
}

// Resolves { state, printers }; printers is empty unless state is OPEN.
// force: ask QZ Tray again instead of reusing the cached list (Refresh).
export async function listPrinters({ force = false } = {}) {
  try {
    if (isDesktopApp()) {
      const printers = await window.electron.listPrinters();
      return { state: PRINT_STATE.OPEN, printers: printers || [] };
    }
    const state = await connectQz();
    if (state !== PRINT_STATE.OPEN) return { state, printers: [] };
    if (cachedQzPrinters && !force) {
      return { state, printers: cachedQzPrinters };
    }
    if (!qzFindPromise) {
      qzFindPromise = qz.printers
        .find()
        .then((found) => {
          cachedQzPrinters = (Array.isArray(found) ? found : [found]).filter(
            Boolean
          );
          return cachedQzPrinters;
        })
        .finally(() => {
          qzFindPromise = null;
        });
    }
    return { state, printers: await qzFindPromise };
  } catch (error) {
    // e.g. the user clicked Block on QZ Tray's prompt.
    lastQzError = String(error?.message || error || "");
    return { state: PRINT_STATE.OPEN, printers: [] };
  }
}

// Picks the saved printer if still installed, else the first TSC one.
export function pickThermalPrinter(printers, saved) {
  if (saved && printers.includes(saved)) return saved;
  return printers.find((name) => /tsc|ttp|tdp|te2|te3/i.test(name)) || "";
}

export async function printTsplLabels(printerName, commands) {
  if (isDesktopApp()) {
    return window.electron.printRaw(printerName, commands);
  }
  if ((await connectQz()) !== PRINT_STATE.OPEN) {
    throw new Error("QZ Tray is not running");
  }
  return qz.print(qz.configs.create(printerName), [
    { type: "raw", format: "command", flavor: "plain", data: commands },
  ]);
}

// TSPL strings are double-quoted; built-in fonts are ASCII only.
function tsplString(text) {
  return String(text ?? "")
    .replace(/₹/g, "Rs.")
    .replace(/"/g, '\\["]')
    // Keeps printable ASCII plus GS (the GS1 separator inside QR data).
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x20-\x7E\x1D]/g, "?");
}

function fit(text, font, widthDots) {
  const maxChars = Math.max(1, Math.floor(widthDots / font.charWidth));
  const value = String(text ?? "");
  return value.length > maxChars ? value.slice(0, maxChars) : value;
}

function wrap(text, font, widthDots, maxLines) {
  const maxChars = Math.max(1, Math.floor(widthDots / font.charWidth));
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) current = next;
    else {
      if (current) lines.push(current);
      current = word.slice(0, maxChars);
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function textCmd(x, y, font, text) {
  return `TEXT ${x},${y},"${font.id}",0,1,1,"${tsplString(text)}"`;
}

// Same encoder (and options) as the A4/HTML barcode, so both paths encode
// byte-identical data. Returns the module pattern as a "1010…" string.
function barcodeModules(value, renderOptions) {
  const target = {};
  JsBarcode(target, value || "0", renderOptions);
  return (target.encodings || []).map((encoding) => encoding.data).join("");
}

// Draws the barcode as one BAR per dark run, each bar a whole number of
// dots wide, centred in the label with its quiet zone.
function barcodeCmds(modules, labelWidth, y, height) {
  const totalModules = modules.length + 2 * QUIET_ZONE_MODULES;
  const moduleDots = Math.max(1, Math.floor(labelWidth / totalModules));
  const startX = Math.max(
    0,
    Math.floor((labelWidth - modules.length * moduleDots) / 2)
  );
  const cmds = [];
  let runStart = -1;
  for (let i = 0; i <= modules.length; i += 1) {
    const dark = modules[i] === "1";
    if (dark && runStart < 0) runStart = i;
    if (!dark && runStart >= 0) {
      cmds.push(
        `BAR ${startX + runStart * moduleDots},${y},${
          (i - runStart) * moduleDots
        },${height}`
      );
      runStart = -1;
    }
  }
  return cmds;
}

function metaParts(item, values, rateText) {
  return [
    values.show.batchNo && item?.item_batch_number,
    values.show.expiryDate &&
      item?.item_exp_date &&
      moment(item.item_exp_date).format("DD/MM/YY"),
    rateText,
  ].filter(Boolean);
}

function captionParts(item, values) {
  return [
    values.show.invoiceDate &&
      item?.created_date &&
      moment(item.created_date).format("DD/MM/YYYY"),
    values.show.distributor && item?.cmr_name,
    values.show.location && item?.bin_location_no,
    values.show.storeName && item?.company,
  ].filter(Boolean);
}

function barcodeLabelCmds(item, values, codeValue, renderOptions, rateText, w, h) {
  const cmds = [];
  const textWidth = w - 2 * PAD;
  let y = PAD;

  cmds.push(textCmd(PAD, y, FONT_MEDIUM, fit(item?.item_name, FONT_MEDIUM, textWidth)));
  y += FONT_MEDIUM.lineHeight;

  const meta = metaParts(item, values, rateText).join("  ");
  if (meta) {
    cmds.push(textCmd(PAD, y, FONT_SMALL, fit(meta, FONT_SMALL, textWidth)));
    y += FONT_SMALL.lineHeight;
  }

  // Bottom lines: human-readable item code, then captions while the
  // barcode still keeps its minimum height.
  const bottom = [item?.item_barcode, ...captionParts(item, values)].filter(
    Boolean
  );
  while (
    bottom.length > 0 &&
    h - PAD - y - 2 - bottom.length * FONT_SMALL.lineHeight < MIN_BARCODE_HEIGHT
  ) {
    bottom.pop();
  }
  const barHeight = Math.max(
    MIN_BARCODE_HEIGHT,
    h - PAD - y - 2 - bottom.length * FONT_SMALL.lineHeight
  );
  cmds.push(
    ...barcodeCmds(barcodeModules(codeValue, renderOptions), w, y + 2, barHeight)
  );
  y += 2 + barHeight + 2;

  bottom.forEach((line) => {
    const text = fit(line, FONT_SMALL, textWidth);
    const x = Math.max(PAD, Math.floor((w - text.length * FONT_SMALL.charWidth) / 2));
    cmds.push(textCmd(x, y, FONT_SMALL, text));
    y += FONT_SMALL.lineHeight;
  });
  return cmds;
}

function qrLabelCmds(item, values, codeValue, rateText, w, h) {
  const cmds = [];
  const available = h - 2 * PAD;
  let cell = 2;
  try {
    const size = QRCode.create(codeValue || "0", {
      errorCorrectionLevel: "M",
    }).modules.size;
    cell = Math.max(1, Math.min(8, Math.floor(available / size)));
    cmds.push(
      `QRCODE ${PAD},${PAD},M,${cell},A,0,"${tsplString(codeValue || "0")}"`
    );
    const textX = PAD + size * cell + PAD;
    const textWidth = w - textX - PAD;
    let y = PAD;
    const lines = [
      ...wrap(item?.item_name, FONT_SMALL, textWidth, 2),
      ...metaParts(item, values, rateText),
      ...captionParts(item, values),
    ];
    lines.forEach((line) => {
      if (y + FONT_SMALL.lineHeight > h - PAD + 2) return;
      cmds.push(textCmd(textX, y, FONT_SMALL, fit(line, FONT_SMALL, textWidth)));
      y += FONT_SMALL.lineHeight;
    });
  } catch {}
  return cmds;
}

function tsplHeader(opts) {
  return [
    `SIZE ${opts.widthMm} mm,${opts.heightMm} mm`,
    `GAP ${opts.gapMm} mm,0 mm`,
    "DIRECTION 1",
    "REFERENCE 0,0",
    // Slower and darker than default gives crisper bar edges.
    "SPEED 3",
    "DENSITY 10",
    "SET TEAR ON",
  ];
}

// True when every word of text fits unbroken in at most maxLines lines.
function fitsWrapped(text, font, widthDots, maxLines) {
  const maxChars = Math.max(1, Math.floor(widthDots / font.charWidth));
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  if (words.some((word) => word.length > maxChars)) return false;
  return wrap(text, font, widthDots, Infinity).length <= maxLines;
}

// The biggest font that shows all of text inside widthDots × heightDots
// (and maxLines), else the smallest one.
function titleFont(text, widthDots, heightDots, maxLines = Infinity) {
  return (
    TITLE_FONTS.find((font) =>
      fitsWrapped(
        text,
        font,
        widthDots,
        Math.min(maxLines, Math.floor(heightDots / font.lineHeight))
      )
    ) || FONT_SMALL
  );
}

// Title centred on one line, barcode filling the rest of the label.
function titledBarcodeCmds(title, codeValue, renderOptions, w, h) {
  const textWidth = w - 2 * PAD;
  // Same words, single-spaced, as titleFont measures them.
  const oneLine = String(title ?? "").split(/\s+/).filter(Boolean).join(" ");
  const font = titleFont(oneLine, textWidth, h - 2 * PAD - 2 - MIN_BARCODE_HEIGHT, 1);
  const text = fit(oneLine, font, textWidth);
  const cmds = [];
  let y = PAD;
  if (text) {
    const x = Math.max(PAD, Math.floor((w - text.length * font.charWidth) / 2));
    cmds.push(textCmd(x, y, font, text));
    y += font.lineHeight;
  }
  const barHeight = Math.max(MIN_BARCODE_HEIGHT, h - PAD - y - 2);
  return [
    ...cmds,
    ...barcodeCmds(barcodeModules(codeValue, renderOptions), w, y + 2, barHeight),
  ];
}

// QR on the left (with its quiet zone, at most half the label wide), title
// wrapped beside it; both centred top to bottom.
function titledQrCmds(title, codeValue, w, h) {
  const available = h - 2 * PAD;
  let size;
  try {
    size = QRCode.create(codeValue || "0", { errorCorrectionLevel: "M" }).modules.size;
  } catch {
    return [];
  }
  const span = size + 2 * QR_QUIET_MODULES;
  const cell = Math.max(
    1,
    Math.min(MAX_QR_CELL_DOTS, Math.floor(h / span), Math.floor(Math.floor(w / 2) / span))
  );
  const quiet = QR_QUIET_MODULES * cell;
  const qrDots = size * cell;
  const cmds = [
    `QRCODE ${quiet},${Math.max(quiet, Math.floor((h - qrDots) / 2))},M,${cell},A,0,"${tsplString(codeValue || "0")}"`,
  ];
  const textX = quiet + qrDots + quiet;
  const textWidth = w - textX - PAD;
  if (textWidth < FONT_SMALL.charWidth) return cmds;
  const font = titleFont(title, textWidth, available);
  const lines = wrap(
    title,
    font,
    textWidth,
    Math.max(1, Math.floor(available / font.lineHeight))
  );
  let y = PAD + Math.max(0, Math.floor((available - lines.length * font.lineHeight) / 2));
  lines.forEach((line) => {
    cmds.push(textCmd(textX, y, font, line));
    y += font.lineHeight;
  });
  return cmds;
}

/**
 * Builds one TSPL job of plain labels that carry just a title and one code —
 * nothing else (e.g. tray labels: tray name + its barcode).
 * @param labels [{ title, codeValue }] in print order
 * @param opts { widthMm, heightMm, gapMm, codeType: "barcode" | "qr", renderOptions }
 */
export function buildCodeLabelTsplJob(labels, opts) {
  const w = Math.round(opts.widthMm * DOTS_PER_MM);
  const h = Math.round(opts.heightMm * DOTS_PER_MM);
  const body = labels.flatMap(({ title, codeValue }) => [
    "CLS",
    ...(opts.codeType === "qr"
      ? titledQrCmds(title, codeValue, w, h)
      : titledBarcodeCmds(title, codeValue, opts.renderOptions, w, h)),
    "PRINT 1,1",
  ]);
  return [...tsplHeader(opts), ...body].join("\r\n") + "\r\n";
}

/**
 * Builds one TSPL job for every label.
 * @param labels [{ item, codeValue }] in print order
 * @param values the modal's form values
 * @param opts { widthMm, heightMm, gapMm, renderOptions, rateText(item) }
 */
export function buildTsplJob(labels, values, opts) {
  const w = Math.round(opts.widthMm * DOTS_PER_MM);
  const h = Math.round(opts.heightMm * DOTS_PER_MM);
  const header = tsplHeader(opts);
  const body = labels.flatMap(({ item, codeValue }) => {
    const rate = opts.rateText(item);
    const rateText = rate != null ? `Rs.${rate}` : null;
    const face =
      values.codeType === "qr"
        ? qrLabelCmds(item, values, codeValue, rateText, w, h)
        : barcodeLabelCmds(
            item,
            values,
            codeValue,
            opts.renderOptions,
            rateText,
            w,
            h
          );
    return ["CLS", ...face, "PRINT 1,1"];
  });
  return [...header, ...body].join("\r\n") + "\r\n";
}
